import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { adminReviewService } from "../services/adminReviewService";
import {
  ReviewDetailResponse,
  ReviewListItem,
  ReviewListRequest,
  ReviewStatus,
} from "../types/review";

export interface UseAdminReviewReturn {
  reviews: ReviewListItem[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  fetchReviews: (request: ReviewListRequest) => Promise<void>;
  getReviewDetail: (messageId: string) => Promise<ReviewDetailResponse | null>;
  updateReview: (
    messageId: string,
    status: ReviewStatus,
    reason: string
  ) => Promise<void>;
  refreshReviews: () => Promise<void>;
}

export const useAdminReview = (): UseAdminReviewReturn => {
  const [reviews, setReviews] = useState<ReviewListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastRequest, setLastRequest] = useState<ReviewListRequest | null>(
    null
  );

  // 검수 목록 조회
  const fetchReviews = useCallback(async (request: ReviewListRequest) => {
    setIsLoading(true);
    setError(null);
    setLastRequest(request);

    try {
      const response = await adminReviewService.getReviews(request);

      if (response.success && response.data) {
        setReviews(response.data.items);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
      } else {
        setError(response.message || "검수 목록 조회에 실패했습니다.");
        toast.error("검수 목록 조회에 실패했습니다.");
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "검수 목록 조회 중 오류가 발생했습니다.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 검수 상세 조회
  const getReviewDetail = useCallback(
    async (messageId: string): Promise<ReviewDetailResponse | null> => {
      try {
        const response = await adminReviewService.getReviewDetail(messageId);

        if (response.success && response.data) {
          return response.data;
        } else {
          toast.error(response.message || "검수 상세 조회에 실패했습니다.");
          return null;
        }
      } catch (error: unknown) {
        toast.error(
          error instanceof Error
            ? error.message
            : "검수 상세 조회 중 오류가 발생했습니다."
        );
        return null;
      }
    },
    []
  );

  // 검수 처리 (승인/거부)
  const updateReview = useCallback(
    async (messageId: string, status: ReviewStatus, reason: string) => {
      try {
        const response = await adminReviewService.updateReview({
          messageId,
          status,
          reviewReason: reason,
        });

        if (response.success) {
          const statusText = status === "APPROVED" ? "승인" : "거부";
          toast.success(`AI 답변이 ${statusText}되었습니다.`);

          // 목록 새로고침
          if (lastRequest) {
            await fetchReviews(lastRequest);
          }
        } else {
          toast.error(response.message || "검수 처리에 실패했습니다.");
        }
      } catch (error: unknown) {
        toast.error(
          error instanceof Error
            ? error.message
            : "검수 처리 중 오류가 발생했습니다."
        );
      }
    },
    [lastRequest, fetchReviews]
  );

  // 목록 새로고침
  const refreshReviews = useCallback(async () => {
    if (lastRequest) {
      await fetchReviews(lastRequest);
    }
  }, [lastRequest, fetchReviews]);

  return {
    reviews,
    isLoading,
    error,
    totalCount,
    totalPages,
    currentPage,
    fetchReviews,
    getReviewDetail,
    updateReview,
    refreshReviews,
  };
};
